// A safe storage adapter that gracefully falls back when Web Storage is unavailable
// (e.g., Safari private mode, Firefox strict privacy, sandboxed iframes)

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const isStorageAvailable = (storage: Storage) => {
  try {
    const testKey = "__storage_test__";
    storage.setItem(testKey, "1");
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

// In-memory fallback store
const mem = new Map<string, string>();
const memoryStorage: StorageLike = {
  getItem: (key) => (mem.has(key) ? mem.get(key)! : null),
  setItem: (key, value) => {
    mem.set(key, value);
  },
  removeItem: (key) => {
    mem.delete(key);
  },
};

let chosenStorage: StorageLike = memoryStorage;

if (typeof window !== "undefined") {
  try {
    if ("localStorage" in window && isStorageAvailable(window.localStorage)) {
      // Wrap native localStorage with try/catch to avoid throwing to callers
      chosenStorage = {
        getItem: (key) => {
          try {
            return window.localStorage.getItem(key);
          } catch {
            return null;
          }
        },
        setItem: (key, value) => {
          try {
            window.localStorage.setItem(key, value);
          } catch {
            // swallow errors in restricted environments
          }
        },
        removeItem: (key) => {
          try {
            window.localStorage.removeItem(key);
          } catch {
            // swallow errors
          }
        },
      } as StorageLike;
    }
  } catch {
    // Accessing window.localStorage itself may throw in some contexts; keep memory fallback
  }
}

export const safeStorage: StorageLike = chosenStorage;
